import { Link } from "react-router-dom";
import styled from "styled-components";
import logo from "../../assets/logo.svg";
import { ShopAccess } from "./access";
import { useShopAccess } from "./useShopAccess";

// The banner sits flush under the navigation bar: the negative margins cancel
// the padding of the main content wrapper so it spans the full viewport width.
const Banner = styled(Link)`
  align-items: center;
  background: #e9e9e9;
  border-bottom: 1px solid #cfcfcf;
  color: #1a1a1a;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: -20px -20px 0;
  padding: 10px 16px;

  &:hover {
    background: #dedede;
  }

  @media (min-width: 768px) {
    padding: 12px 24px;
  }
`;

const Message = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  text-decoration: underline;
  text-transform: uppercase;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

// The shared logo is white artwork for the black header: invert it so it reads
// as dark on the grey band.
const BannerLogo = styled.img`
  filter: invert(1);
  flex: 0 0 auto;
  height: 22px;
  width: auto;
`;

// Balances the logo so the message stays optically centred.
const Spacer = styled.span`
  flex: 0 0 auto;
  width: 39px;
`;

// What the banner says, and where it takes the account, for each state that
// cannot buy yet.
const NOTICE_BY_ACCESS: Partial<
  Record<ShopAccess, { message: string; to: string }>
> = {
  visitor: {
    message: "Per acquistare dallo shop, registrati o effettua il login",
    to: "/login",
  },
  registered: {
    message:
      "Lo shop è riservato ai tesserati: tessera la tua famiglia per la stagione in corso",
    to: "/account/membership",
  },
};

type AccessNoticeViewProps = {
  access: ShopAccess;
};

/**
 * Tells an account that cannot buy yet why, and how to become able to. The
 * whole message is the call to action, and it shows on every shop page until
 * the family has a membership valid for the current season.
 */
export const AccessNoticeView = ({ access }: AccessNoticeViewProps) => {
  const notice = NOTICE_BY_ACCESS[access];

  if (!notice) {
    return null;
  }

  return (
    <Banner to={notice.to}>
      <Spacer aria-hidden="true" />
      <Message>{notice.message}</Message>
      <BannerLogo src={logo} alt="" />
    </Banner>
  );
};

export const AccessNotice = () => {
  const access = useShopAccess();

  return <AccessNoticeView access={access} />;
};
